import {DependencyList, useCallback, useEffect, useState} from 'react';
import {useIsMount} from '../useIsMount';

export interface UseFirestoreLoadOptions {
  /** When true, the load function is not run and refetch is a no-op. */
  skip?: boolean;
  /** Dependencies that trigger useCallback when changed. Defaults to [listFn]. */
  deps?: DependencyList;
}

export interface UseFirestoreLoadResult<T> {
  /** data is the result of the function */
  data: T | undefined;
  /** loading is true when the function is running */
  loading: boolean;
  /** error is the error of the function */
  error: Error | undefined;
  /** refetch re-runs the load function with the same arguments as load (same signature) */
  refetch: () => Promise<void>;
}

/**
 * run an async function on mount and set the data to data variable
 * does not throw error, but will return error and loading state
 * refetch function to re-run
 */
export function useFunctionList<T>(
  listFn: () => Promise<T>,
  options: UseFirestoreLoadOptions = {},
): UseFirestoreLoadResult<T> {
  const {skip = false, deps = []} = options;
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const isMounted = useIsMount();

  const refetch = useCallback(async () => {
    if (skip) {
      setLoading(false);
      setError(undefined);
      return;
    }
    setLoading(true);
    setError(undefined);

    try {
      const result = await listFn();
      if (isMounted()) {
        setData(result);
        setError(undefined);
      }
    } catch (err) {
      if (isMounted()) {
        setData(undefined);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, listFn, isMounted, ...deps]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
